/**
 * Pure derivation of the PortfolioGrowthHero's display data from an
 * analytics payload.
 *
 * Why this is a separate module: the hero renders a member's MONEY. A
 * confident `$0` is indistinguishable from a real zero balance, so every
 * path that cannot produce a trustworthy number must resolve to an
 * explicit "unavailable" state instead of falling back to zero.
 * `deriveHeroData` returns `null` for exactly those paths and the caller
 * renders the unavailable card — see PortfolioGrowthHero.
 *
 * A real zero (`current_balance: 0`) is NOT one of those paths: it is a
 * legitimate value and renders as `$0`.
 */

/**
 * Coerce to a finite number, or `null` when the value cannot be trusted.
 *
 * Deliberately stricter than `Number(v) || 0`:
 *   - `undefined` / `null` / `""` → null (absent, not zero)
 *   - `NaN` / `Infinity`          → null (arithmetic would poison the UI)
 *   - `true` / `false`            → null (not money: `Number(true)` is 1)
 *   - `0` / `"0"`                 → 0    (a real zero survives)
 *
 * The boolean case is STARSEED-CAPMARKETS-162 (#2420 re-review): a cached
 * partner row carrying `balance: true` rendered on the partner account page as
 * $1.00, while every API surface already read it as not a money value.
 */
export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Map an analytics payload to the hero's view model.
 *
 * @param {"me"|"platform"} scope   which endpoint produced `payload`
 * @param {object}          payload response body from /analytics/me or
 *                                  /analytics/summary
 * @returns {object|null} the view model, or `null` when the payload cannot
 *                        support a trustworthy balance (caller MUST then
 *                        render the unavailable state, never a zero).
 *
 * `combined_balance` is the member's TOTAL position — the Native Fund plus
 * partner-held capital. It was Native-Fund-only until CAPMARKETS-235, when a
 * member whose capital is entirely partner-held was shown "MY PORTFOLIO $0.00"
 * above a partner-held figure of $335,277.82. FORGE-20260522-001's exclusion is
 * amended, not ignored: the split is carried in `native_balance` /
 * `partner_balance` so the page can still say which custodian holds what.
 *
 * Two fields exist so a caller cannot state something the model does not know:
 *
 *   `is_combined`  the total draws on more than one source.
 *   `pnl_scope`    "all" when `combined_pnl` / `return_pct` cover the whole
 *                  displayed total; "native_only" when they do not. There is no
 *                  trustworthy partner-side P&L (CAPMARKETS-190), so rendering
 *                  the percentage without checking this states a return on money
 *                  that return was never computed over.
 */
export const deriveHeroData = (scope, payload) => {
  if (!payload || typeof payload !== "object") return null;

  const native = payload.native;
  if (!native || typeof native !== "object") return null;

  // `net_performance` (platform scope) and `net_pnl` (personal scope) are
  // the same quantity under two names — see analytics_service.py.
  const rawPnl = scope === "platform" ? native.net_performance : native.net_pnl;

  const balance = toFiniteNumber(native.current_balance);
  const pnl = toFiniteNumber(rawPnl);
  const basis = toFiniteNumber(native.total_deposits);

  // Any missing component means we cannot state the member's position.
  if (balance === null || pnl === null || basis === null) return null;

  const partner = derivePartnerProvenance(payload.partner);

  // STARSEED-CAPMARKETS-235 — the hero used to be Native-Fund-only, and
  // `combined_balance` was `balance` with partner capital carried alongside but
  // never added in (FORGE-20260522-001).
  //
  // For a member whose money is entirely partner-held that produced a dashboard
  // reading "MY PORTFOLIO $0.00" above a partner-held figure of $335,277.82.
  // Every number on the page was correct and correctly labelled; the page still
  // told a funded member his portfolio was zero, because the largest type on the
  // screen said so. That is CAPMARKETS-181's harm arriving through visual
  // HIERARCHY rather than through a value — the third variant of "a zero can be
  // true and still be a lie" in this integration, after the absent field (181)
  // and the PAMM-emptied account (190).
  //
  // Amended deliberately, on the operator's decision (2026-09-04), not deviated
  // from silently: the hero is now the member's total position.
  const partnerBalance = partner ? toFiniteNumber(partner.balance) : null;
  const combined = partnerBalance === null ? balance : balance + partnerBalance;

  const model = {
    combined_balance: combined,
    native_balance: balance,
    partner_balance: partnerBalance,
    // TRUE only when the total is made of more than one source. The caller uses
    // it to decide whether a single-source growth figure may be shown at all.
    is_combined: partnerBalance !== null,
    combined_pnl: pnl,
    return_pct: basis > 0 ? (pnl / basis) * 100 : 0,
    // P&L and return describe the NATIVE fund alone — there is no trustworthy
    // partner-side P&L (CAPMARKETS-190: the partner reports 0.00 on pooled
    // accounts, and realised performance lives in a PAMM system we cannot
    // read). Presenting a native-only percentage beside a combined total would
    // state a return on money that return was never computed over.
    //
    // So the scope is named rather than assumed. A caller that renders
    // `return_pct` without checking this is stating something false.
    pnl_scope: partnerBalance === null ? "all" : "native_only",
    partner: partner,
  };
  if (scope === "platform") {
    model.qualified_users = payload.qualified_users;
  }
  return model;
};

/**
 * Map the analytics payload's `partner` block to a disclosure view model.
 *
 * STARSEED-CAPMARKETS-115. A member is about to see a number that a human
 * read off a screenshot and typed into a CSV. It renders in the same
 * typeface, with the same authority, as a figure synced from an API — and it
 * must not be able to.
 *
 * This is the same argument `toFiniteNumber` makes one step further out. That
 * one keeps "absent" distinguishable from "zero"; this keeps "transcribed"
 * distinguishable from "synced", and "as of last Tuesday" from "as of now".
 *
 * Returns `null` when there is no partner-held capital at all — which is a
 * different fact from a balance of zero, and the caller must render neither
 * a figure nor a disclosure for it.
 *
 * The balance is deliberately allowed through as `null` while `as_of` and
 * `confidence` are present: that is a real state (an account whose statement
 * column was blank) and the disclosure still applies to it.
 */
export const derivePartnerProvenance = (partner) => {
  if (!partner || typeof partner !== "object") return null;

  const balance = toFiniteNumber(partner.balance);
  const equity = toFiniteNumber(partner.equity);
  const accounts = toFiniteNumber(partner.accounts_count);

  // A block with no accounts is not partner-held capital, it is an empty
  // response. Rendering a disclosure for it would attach provenance to
  // nothing.
  if (!accounts) return null;

  return {
    balance,
    equity,
    accounts_count: accounts,
    // `as_of` is the STATEMENT's date and, across several accounts, the
    // stalest of them — the API computes it that way on purpose. A total is
    // only as current as its most out-of-date input, and showing the newest
    // would let one freshly-imported account make a month-old total look
    // current.
    as_of: partner.as_of || null,
    confidence: partner.confidence || null,
    source: partner.source || null,
    // CAPMARKETS-287. Derived server-side and passed through untouched: the
    // browser must not re-implement the staleness policy, or the member's
    // screen and the operator's audit could disagree about whether the same
    // figure is current.
    freshness: partner.freshness || null,
  };
};

/**
 * Human-readable provenance for a partner figure: how it was obtained, and
 * how much that method warrants trusting it.
 *
 * Keyed as an exhaustive map rather than a `switch` with a default, so adding
 * a confidence level to the model is a visible change here instead of
 * silently rendering the fallback forever.
 */
export const CONFIDENCE_DISCLOSURE = {
  transcribed: {
    label: "Transcribed from a partner statement",
    detail: "Read from a partner-provided statement and entered by hand.",
  },
  partner_entered: {
    label: "Entered by the partner",
    detail: "Typed into this platform by the partner who holds the account.",
  },
  exported: {
    label: "Exported from a partner statement",
    detail: "Taken from a file the partner produced.",
  },
  // STARSEED-CAPMARKETS-217. Deliberately NOT `api`: that label asserts the
  // partner reported the figure. This one is computed here from the movements
  // the partner recorded, because the partner's balance fields read 0.00 on
  // every account (CAPMARKETS-190). Measured accuracy on 2026-08-28: one
  // member within 1%, three between 7% and 9% out, in both directions. The
  // label has to carry that, because the number cannot.
  ledger_derived: {
    label: "Calculated from your transaction history",
    detail:
      "Worked out here by adding up every deposit, withdrawal and adjustment " +
      "the partner recorded on your accounts — not a figure the partner " +
      "reported. Treat it as an estimate and open Transactions in the " +
      "sidebar to see the movements behind it.",
  },
  api: {
    label: "Synced from the partner",
    detail: "Retrieved directly from the partner's system.",
  },
};

/**
 * Human-readable provenance for the PATH a partner figure took.
 *
 * STARSEED-CAPMARKETS-198. Distinct from `CONFIDENCE_DISCLOSURE`, which grades
 * the artifact; this grades who produced the figure and by what route. A number
 * the custodian typed into our console is a stronger claim than one a human read
 * off the custodian's screenshot, and the member should be able to tell them
 * apart — until now the analytics payload hardcoded `"partner_statement"` for
 * every partner block, so they read identically.
 *
 * Exhaustive by construction, for the same reason `CONFIDENCE_DISCLOSURE` is:
 * adding a source to the model must be a visible change here rather than a
 * silent fallback forever. `describeSource` returns `null` for an unknown key so
 * the caller renders nothing rather than something wrong.
 */
export const SOURCE_DISCLOSURE = {
  partner_api: {
    label: "Synced from the partner",
    detail: "Retrieved directly from the partner's system.",
  },
  partner_console: {
    label: "Entered by the partner",
    detail: "Typed into this platform by the partner who holds the account.",
  },
  partner_statement_csv: {
    label: "From a partner statement file",
    detail: "Taken from a file the partner produced.",
  },
  partner_statement_manual: {
    label: "Transcribed from a partner statement",
    detail: "Read from a partner-provided statement and entered by hand.",
  },
};

/**
 * The disclosure for a source, or `null` when we do not recognise it.
 *
 * Null rather than a fallback: a figure whose provenance we cannot name must
 * render without a provenance claim, never with a generic one that quietly
 * upgrades an unknown path to a plausible-sounding known one.
 */
export const describeSource = (source) => {
  if (!source || typeof source !== "string") return null;
  return SOURCE_DISCLOSURE[source] || null;
};

/**
 * Format an ISO `as_of` for display, or `null` when it cannot be trusted.
 *
 * Returning `null` rather than a placeholder is the same rule as everywhere
 * else in this module: a date we cannot parse must not render as though we
 * know when the figure is from.
 *
 * **Rendered in UTC, deliberately.** `as_of` is a calendar date — the day
 * printed on the partner's statement — that CAPMARKETS-112 normalizes to
 * midnight UTC. Formatting that instant in the viewer's local zone shows the
 * PREVIOUS day to everyone west of UTC: a member in New York would see a
 * statement dated 2026-08-04 rendered as "Aug 3, 2026". This whole module
 * exists to stop a figure claiming something untrue about itself, and a date
 * that is off by one is exactly that. (Copilot, PR #2358.)
 */
export const AS_OF_FORMAT = {
  year: "numeric",
  month: "short",
  day: "numeric",
  // Load-bearing. Exported so a test can assert its PRESENCE structurally:
  // on a UTC runner (which CI is) local time and UTC are identical, so no
  // behavioural assertion can observe this option going missing. A guard that
  // cannot fail where it matters is the thing this module keeps finding.
  timeZone: "UTC",
};

export const formatAsOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, AS_OF_FORMAT);
};
